import {ReactElement, useContext} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {csvBoxExtractFilename, csvBoxExtractRows, csvBoxGeneralError} from "../../../../../global/csv_box_helpers";
import {CSVBoxButton} from "../../../../../components/csvbox/CSVBoxButton";
import {AuthContext} from "../../../../../contexts/AuthContext";

export interface ManualPropertyRentRollDataItem {
    unitType: string;
    unit: string;
    squareFeet: string;
    scheduledRent: string;
    marketRent: string;
    leaseStart: string | null;
    leaseEnd: string | null;
}

function parseNumberField(raw: unknown): string | null  {
    if(typeof raw === "number") {
        return raw.toString();
    } else if(typeof raw === "string") {
        if(raw === "" || isNaN(parseFloat(raw))) {
            return null;
        }
        return raw;
    } else {
        return null;
    }
}

function extractData(values: unknown[]): ManualPropertyRentRollDataItem[] {
    const retVals: ManualPropertyRentRollDataItem[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;
        const virtualData = row["_virtual_data"] as Record<string, unknown> | undefined;

        if(typeof virtualData !== "object" || virtualData === null || virtualData === undefined) {
            continue;
        }

        const unitType = row["Unit Type"];
        const unit = row["Unit"];
        const squareFeetRaw = row["Square Feet"];
        const scheduledRentRaw = row["Scheduled Rent"];
        const marketRentRaw = row["Market Rent"];
        const leaseStart = virtualData["Lease Start Date"];
        const leaseEnd = virtualData["Lease End Date"];

        // Check string fields
        if(typeof unitType !== "string") {
            continue;
        }

        if(typeof unit !== "string") {
            continue;
        }

        if(typeof leaseStart !== "string" && leaseStart !== null && leaseStart !== undefined) {
            continue;
        }

        if(typeof leaseEnd !== "string" && leaseEnd !== null && leaseEnd !== undefined) {
            continue;
        }

        // Parse and validate number fields
        const squareFeet = parseNumberField(squareFeetRaw);
        if(squareFeet === null) {
            continue;
        }

        const scheduledRent = parseNumberField(scheduledRentRaw);
        if(scheduledRent === null) {
            continue;
        }

        const marketRent = parseNumberField(marketRentRaw);
        if(marketRent === null) {
            continue;
        }

        retVals.push({
            unitType: unitType,
            unit: unit,
            squareFeet: squareFeet,
            scheduledRent: scheduledRent,
            marketRent: marketRent,
            leaseStart: leaseStart === undefined || leaseStart === "" ? null : leaseStart,
            leaseEnd: leaseEnd === undefined || leaseEnd === "" ? null : leaseEnd
        });
    }

    return retVals;
}

export interface ManualPropertyRentRollButtonProps {
    dataUploaded: (data: ManualPropertyRentRollDataItem[], filename: string) => void;
    disabled: boolean;
}

export function ManualPropertyRentRollButton(props: ManualPropertyRentRollButtonProps): ReactElement {
    const {user} = useContext(AuthContext);

    function handleData(data: unknown): void {
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("rent roll");
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("rent roll");
            return;
        }

        const filename = csvBoxExtractFilename(data);

        props.dataUploaded(extracted, filename ?? "unknown");
    }

    return <CSVBoxButton
        licenseKey="PzwkB8EnuRrZTo46JXOu3XPYKhOuxk"
        user={{
            userId: user?.id ?? "unknown",
            name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
            email: user?.email ?? "unknown",
            clientId: user?.clientId ?? "unknown",
            clientName: user?.clientName ?? "unknown",
        }}
        onImport={(result, data) => {
            if(result) {
                handleData(data);
            } else {
                csvBoxGeneralError("rent roll");
            }
        }}
        render={(launch, isLoading) => {
            return <Button onClick={launch} disabled={isLoading || props.disabled}>Upload Rent Roll</Button>;
        }}
    />;
}